import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Film, Trash2, Loader2, Send, Pencil } from "lucide-react";
import { MetaAdButton } from "@/components/ads/MetaAdButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { PublishDialog } from "@/components/creatives/PublishDialog";
import { MusicDialog } from "@/components/creatives/MusicDialog";
import { TextVideoDialog } from "@/components/creatives/TextVideoDialog";
import { AR_STYLE } from "@/components/creatives/ChatMessage";
import { DriveExportButton } from "@/components/creatives/DriveExportButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/creatives`;

const VideoDialog = ({ item, onClose, onStarted }) => {
  const [prompt, setPrompt] = useState("");
  const [duration, setDuration] = useState("5");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/${item.id}/video`, { prompt, duration });
      toast.success("Video-ul se generează — durează 2–5 minute");
      onStarted(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="video-dialog" className="max-w-md">
        <DialogHeader><DialogTitle>Fă video din această imagine</DialogTitle></DialogHeader>
        <img src={item.url} alt="" className="aspect-square w-full rounded-lg object-cover" />
        <Textarea data-testid="video-prompt-input" value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={3}
          placeholder="Mișcarea (opțional): ex. camera se apropie lent, aburul iese din aparat" />
        <div className="flex gap-2">
          {["5", "10"].map((d) => (
            <button key={d} data-testid={`video-duration-${d}`} onClick={() => setDuration(d)} className={`flex-1 rounded-full border py-1.5 text-sm ${duration === d ? "border-amber-400 bg-amber-400 text-zinc-950" : "border-zinc-700"}`}>{d} secunde</button>
          ))}
        </div>
        <button data-testid="video-generate-btn" onClick={go} disabled={busy} className="flex items-center justify-center gap-2 rounded-full bg-amber-400 py-2.5 text-sm font-semibold text-zinc-950 disabled:opacity-60">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Film className="h-4 w-4" />} Generează video
        </button>
      </DialogContent>
    </Dialog>
  );
};

export const CreativeGallery = ({ items, reload, onEdit }) => {
  const [tab, setTab] = useState("all");
  const [videoFor, setVideoFor] = useState(null);
  const [pub, setPub] = useState(null);
  const [mus, setMus] = useState(null);
  const [txt, setTxt] = useState(null);
  const shown = items.filter((i) => tab === "all" || i.kind === tab);
  const upscale = async (id) => {
    try { await axios.post(`${API}/${id}/upscale`); toast.success("Mărire 4K pornită — apare în galerie în ~1 minut"); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const del = async (id) => { if (!window.confirm("Ștergi creativul?")) return; await axios.delete(`${API}/${id}`); reload(); };

  return (
    <div className="space-y-4" data-testid="creative-gallery">
      <div className="flex items-center gap-2">
        <h2 className="mr-3 text-base font-semibold text-zinc-100">Galerie</h2>
        {[["all", "Toate"], ["image", "Imagini"], ["video", "Video"]].map(([k, l]) => (
          <button key={k} data-testid={`gallery-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-3 py-1 text-xs ${tab === k ? "bg-zinc-100 text-zinc-950" : "text-zinc-400 hover:text-zinc-200"}`}>{l}</button>
        ))}
      </div>
      {shown.length === 0 && <p className="text-sm text-zinc-500" data-testid="gallery-empty">Nimic generat încă.</p>}
      <div className="grid grid-cols-2 items-start gap-4 md:grid-cols-3 xl:grid-cols-5">
        {shown.map((it) => (
          <div key={it.id} data-testid={`creative-item-${it.id}`} className="group overflow-hidden rounded-xl border border-zinc-800 bg-zinc-900">
            <div className="relative">
              {it.kind === "video"
                ? <video src={it.url} controls loop muted playsInline style={{ aspectRatio: AR_STYLE[it.aspect_ratio] || "1 / 1" }} className="w-full bg-black object-cover" />
                : <img src={`${it.url}?w=480`} alt="" loading="lazy" style={{ aspectRatio: AR_STYLE[it.aspect_ratio] || "1 / 1" }} className="w-full object-cover" />}
              <button data-testid={`delete-${it.id}`} title="Șterge" onClick={() => del(it.id)} className="absolute left-2 top-2 rounded-full bg-zinc-950/75 p-2 text-zinc-100 shadow backdrop-blur transition-colors hover:bg-rose-500 hover:text-white"><Trash2 className="h-4 w-4" /></button>
              <a data-testid={`download-${it.id}`} title="Descarcă" href={`${it.url}?download=1`} className="absolute right-2 top-2 rounded-full bg-zinc-950/75 p-2 text-zinc-100 shadow backdrop-blur transition-colors hover:bg-amber-400 hover:text-zinc-950"><Download className="h-4 w-4" /></a>
            </div>
            <div className="flex flex-wrap items-center gap-1 p-2">
              <span className="flex-1 truncate text-[11px] text-zinc-400">{it.sku || ""} · {it.aspect_ratio || "1:1"} · {it.kind === "video" ? "video" : "imagine"}{it.published?.length ? " · publicat" : ""}</span>
              {it.kind === "image" && !it.upscaled && <button data-testid={`gallery-upscale-${it.id}`} title="Mărește la 4K (~$0.01)" onClick={() => upscale(it.id)} className="rounded px-1.5 py-1 text-[10px] font-bold text-zinc-400 hover:bg-zinc-800 hover:text-amber-400">→4K</button>}
              {it.kind === "video" && !it.music && <button data-testid={`gallery-music-${it.id}`} title="Adaugă muzică AI" onClick={() => setMus(it)} className="rounded px-1.5 py-1 text-xs text-zinc-400 hover:bg-zinc-800 hover:text-amber-400">♪</button>}
              {it.kind === "video" && !it.text_overlay && <button data-testid={`gallery-text-${it.id}`} title="Text animat (titlu, preț, îndemn)" onClick={() => setTxt(it)} className="rounded px-1.5 py-1 text-[10px] font-bold text-zinc-400 hover:bg-zinc-800 hover:text-amber-400">Aa</button>}
              {it.text_overlay && <span data-testid={`gallery-badge-text-${it.id}`} title="Are text animat" className="rounded bg-amber-400 px-1 text-[10px] font-bold text-zinc-950">Aa</span>}
              {it.music && <span className="rounded bg-amber-400 px-1 text-[10px] font-bold text-zinc-950">♪</span>}
              {it.upscaled && <span className="rounded bg-amber-400 px-1 text-[10px] font-bold text-zinc-950">4K</span>}
              <MetaAdButton assetId={it.id} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-amber-400" />
              <button data-testid={`gallery-edit-${it.id}`} title="Modifică în chat" onClick={() => onEdit(it)} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-amber-400"><Pencil className="h-4 w-4" /></button>
              <button data-testid={`gallery-publish-${it.id}`} title="Publică pe Facebook/Instagram" onClick={() => setPub(it)} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-amber-400"><Send className="h-4 w-4" /></button>
              {it.kind === "image" && <button data-testid={`make-video-${it.id}`} title="Fă video" onClick={() => setVideoFor(it)} className="rounded p-1.5 text-zinc-400 hover:bg-zinc-800 hover:text-amber-400"><Film className="h-4 w-4" /></button>}
              <DriveExportButton item={it} />
            </div>
          </div>
        ))}
      </div>
      {videoFor && <VideoDialog item={videoFor} onClose={() => setVideoFor(null)} onStarted={reload} />}
      {pub && <PublishDialog asset={pub} onClose={() => setPub(null)} onDone={reload} />}
      {mus && <MusicDialog asset={mus} onClose={() => setMus(null)} onStarted={reload} />}
      {txt && <TextVideoDialog asset={txt} onClose={() => setTxt(null)} onStarted={reload} />}
    </div>
  );
};
